/* eslint-disable react-hooks/exhaustive-deps */
import { useContext, useEffect, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { useSelector } from 'react-redux';
import get from 'lodash/get';
import isEqual from 'lodash/isEqual';
import PropTypes from 'prop-types';

import { RiskContext } from 'forms';
import { RiskDetailsContext } from 'pages/RiskDetails/RiskDetails';

// app
import * as utils from 'utils';

// app
import FormAutocompleteMuiAsyncView from './FormAutocompleteMuiAsync.view';

FormAutocompleteMuiAsync.propTypes = {
  control: PropTypes.object,
  name: PropTypes.string.isRequired,
  options: PropTypes.arrayOf(PropTypes.object).isRequired,
  optionKey: PropTypes.string.isRequired,
  optionLabel: PropTypes.string.isRequired,
  optionsCreatable: PropTypes.bool,
  optionsFetch: PropTypes.object,
  value: PropTypes.oneOfType([PropTypes.array, PropTypes.object]),
  defaultValue: PropTypes.oneOfType([PropTypes.array, PropTypes.object]),
  label: PropTypes.string,
  placeholder: PropTypes.string,
  hint: PropTypes.string,
  error: PropTypes.shape({
    message: PropTypes.string,
    type: PropTypes.string,
  }),
  muiComponentProps: PropTypes.object,
  callback: PropTypes.func,
};

FormAutocompleteMuiAsync.defaultProps = {
  muiComponentProps: {},
  options: [],
  optionKey: 'value',
  optionLabel: 'label',
  value: null,
  defaultValue: null,
};

export default function FormAutocompleteMuiAsync(props) {
  const { setValue, watch } = useFormContext();
  const [dependentFieldsState, setDependentFieldsState] = useState(props?.dependentFieldsValues);
  const [componentOptions, setComponentOptions] = useState([]);
  const {
    dataIndex,
    dataSource,
    valueIndex,
    dataIncludeCustom,
    dependants,
    dependentFieldsValues,
    name,
    options,
    partialMatch,
    defaultValue,
  } = props;
  const { risk } = useContext(RiskContext);
  const { risk: riskDetails } = useContext(RiskDetailsContext);
  const product = risk?.riskType ?? riskDetails?.riskType;
  const endpoint = useSelector((state) => get(state, 'config.vars.endpoint'));
  const isMultiple = props?.muiComponentProps?.multiple;

  useEffect(() => {
    if (isEqual(dependentFieldsValues, dependentFieldsState)) return;

    setDependentFieldsState(dependentFieldsValues);
  }, [dependentFieldsValues]);

  useEffect(() => {
    let isSubscribed = true;

    const fetchData = async () => {
      try {
        const response = await utils.api.get({
          endpoint: endpoint.auth,
          path: `api/v1/products/${product}/data/${dataIndex}`,
          params: {
            dataSource,
            valueIndex,
            ...(typeof partialMatch === 'boolean' && { partialMatch }),
            ...(dataSource && defaultValue && { defaultValue }),
          },
        });
        const data = await utils.api.handleResponse(response);
        isSubscribed && setComponentOptions(data);
      } catch (err) {
        const errorParams = {
          file: 'FormAutocompleteMuiAsync.js',
          message: 'API fetch error, initial fetchData',
        };

        utils.api.handleError(err, errorParams);
      }
    };
    dependants.length === 0 && fetchData();

    const selectedValue = watch(name);
    if (selectedValue && options) {
      setComponentOptions(options);
    }

    return () => (isSubscribed = false);
  }, []);

  useEffect(() => {
    let isSubscribed = true;

    const fetchData = async () => {
      try {
        let restrictBy = ``;
        for (const dependant of dependants) {
          const [dependantIndex] = dependant.split(':');
          restrictBy = `${restrictBy}${dependantIndex}:${dependentFieldsState[dependantIndex]?.value};`;
        }
        const response = await utils.api.get({
          endpoint: endpoint.auth,
          path: `api/v1/products/${product}/data/${dataIndex}`,
          params: {
            dataSource,
            valueIndex,
            restrictBy: encodeURIComponent(restrictBy),
            ...(dataIncludeCustom ? { includeCustom: dataIncludeCustom } : {}),
            ...(typeof partialMatch === 'boolean' && { partialMatch }),
            ...(dataSource && defaultValue && { defaultValue }),
          },
        });
        const data = await utils.api.handleResponse(response);

        const selectedValue = watch(name);
        isSubscribed && setComponentOptions(data);
        if (data?.length === 1 && !isMultiple) {
          setValue(name, data[0].value);
        } else if (utils.generic.isValidArray(data, true) && selectedValue) {
          const foundInOptions = isMultiple
            ? selectedValue.every((item) => data.find((dataItem) => dataItem?.value === item?.value || dataItem?.value === item))
            : data.find((item) => selectedValue?.value === item?.value || selectedValue === item?.value) || false;

          if (!foundInOptions && isSubscribed) setValue(name, isMultiple ? [] : null);
        }
      } catch (err) {
        const errorParams = {
          file: 'FormAutocompleteMuiAsync.js',
          message: 'API fetch error',
        };

        utils.api.handleError(err, errorParams);
      }
    };

    const isAllDefined = Object.keys(dependentFieldsState).every((key) => utils.generic.isValidObject(dependentFieldsState[key]));
    if (isAllDefined && dependants.length > 0) {
      isSubscribed && setComponentOptions([]);
      fetchData();
    }

    return () => (isSubscribed = false);
  }, [dependentFieldsState]);

  return <FormAutocompleteMuiAsyncView componentOptions={componentOptions} {...props} />;
}
